import { effect } from "@preact/signals";
import * as Preact from "preact";

import * as modalController from "modalController";
import { cn } from "shared/utils";
import stores from "stores/stores";
import * as tts from "tts";
import * as speakingModeControls from "views/speakingModeControls";
import * as ttsUi from "views/ttsUi";
import TTSVoicePickerView from "views/TTSVoicePickerView";
import * as browserTTSVoices from "../browserTTSVoices";

interface Props {
  language: string;
  /**
   * Section heading rendered above the voice settings (used on the settings
   * page). Omitted in the reader sidebar, which has its own layout. Rendered
   * inside this component so it's hidden together with the controls when the
   * language has no TTS support.
   */
  heading?: string;
  /**
   * Whether the reader's global Read Aloud playback is active. Drives the
   * Read Aloud activity indicator and locks the Auto-Pronounce controls
   * while reading aloud.
   */
  readAloudPlaybackActive?: boolean;
  /**
   * Called after the Read Aloud voice or rate changes. The reader passes its
   * restart-playback-or-play-demo handler. When absent (settings page), a
   * rate change previews the resolved voice at the new rate instead.
   */
  onReadAloudSettingsChanged?: () => void;
  /**
   * Layout variant. The reader sidebar ("sidebar", the default) uses the
   * compact pill controls. The settings page ("settings") uses native form
   * controls — a select for speed, a checkbox for the Auto-Pronounce toggle,
   * and a plain "Change…" link for the voice picker — to match the basic
   * styling of the rest of /settings.
   */
  variant?: "sidebar" | "settings";
}

/**
 * Voice settings for a language: Read Aloud voice and rate, plus the
 * Auto-Pronounce toggle, voice, and rate. Shared between the reader sidebar
 * ("Aa" tab) and the settings page. Renders nothing while TTS is unsupported
 * for both features.
 */
class TTSVoiceSettingsView extends Preact.Component<Props> {
  private disposeSpeakingModeEffect?: () => void;
  private disposeTtsAvailabilityEffect?: () => void;
  private disposeTtsPlaybackEffect?: () => void;
  private disposeTtsVoicesEffect?: () => void;

  componentDidMount() {
    // Prime browser voices so availability resolves and stored device voice
    // names display (no-op when the page already initialized this language).
    void tts.initialize(this.props.language).then(() => this.forceUpdate());

    // Re-render when server-synced fields change (signals don't
    // auto-subscribe class components).
    this.disposeSpeakingModeEffect = effect(() => {
      void stores.user.state.value.speakingMode;
      this.forceUpdate();
    });
    this.disposeTtsVoicesEffect = effect(() => {
      void stores.user.state.value.ttsVoices;
      this.forceUpdate();
    });
    this.disposeTtsAvailabilityEffect = effect(() => {
      // Both features gate this section, so subscribe to each —
      // noWordBoundaries languages only ever reach "supported" via readAloud.
      void tts.getAvailabilitySignal({
        user: stores.user,
        feature: "speakingMode",
        language: this.props.language,
      }).value;
      void tts.getAvailabilitySignal({
        user: stores.user,
        feature: "readAloud",
        language: this.props.language,
      }).value;
      this.forceUpdate();
    });
    this.disposeTtsPlaybackEffect = effect(() => {
      void tts.activeFeature.value;
      this.forceUpdate();
    });
    browserTTSVoices.onChange(this.handleChangeVoice);
  }

  componentWillUnmount() {
    this.disposeSpeakingModeEffect?.();
    this.disposeTtsAvailabilityEffect?.();
    this.disposeTtsPlaybackEffect?.();
    this.disposeTtsVoicesEffect?.();
    browserTTSVoices.offChange(this.handleChangeVoice);
  }

  handleChangeVoice = () => {
    this.forceUpdate();
  };

  isSupported = (feature: tts.TTSFeature) =>
    tts.getAvailabilitySignal({
      user: stores.user,
      feature,
      language: this.props.language,
    }).value === "supported";

  render() {
    const { language } = this.props;
    // The Read Aloud row stays rendered for users who can use high quality
    // voices elsewhere so they can discover that Plus unlocks high quality
    // Read Aloud playback. noWordBoundaries languages (zh/ja/...) only reach
    // Read Aloud, not Auto-Pronounce, so gating solely on speakingMode would
    // hide the whole section — and the Read Aloud voice picker — for them.
    if (!this.isSupported("speakingMode") && !this.isSupported("readAloud")) {
      return null;
    }
    const activeTtsFeature = tts.activeFeature.value;
    const readAloudActive =
      Boolean(this.props.readAloudPlaybackActive) ||
      activeTtsFeature === "readAloud";
    const speakingModeActive = activeTtsFeature === "speakingMode";
    const speakingModeControlsDisabled = readAloudActive;
    // Auto-Pronounce is unreachable for noWordBoundaries languages (zh/ja/...),
    // where only Read Aloud is available. Hide its row rather than show a voice
    // picker with no selectable voices.
    const speakingModeSupported = this.isSupported("speakingMode");

    const openPicker = (feature: tts.TTSFeature) => {
      if (feature === "speakingMode" && speakingModeControlsDisabled) return;
      modalController.show(
        <TTSVoicePickerView
          language={language}
          feature={feature}
          close={() => {
            modalController.cancelAll();
            this.forceUpdate();
            // Only the Read Aloud picker triggers the
            // restart-or-demo: Auto-Pronounce's picker already plays its
            // own confirmation on row-click, and there's no analogous
            // "global playback" to restart. Running the readAloud demo
            // here would speak the readAloud voice/rate ("Google
            // español" slowly) right after the user just picked an HD
            // Auto-Pronounce voice — confusing.
            if (feature === "readAloud") {
              this.props.onReadAloudSettingsChanged?.();
            }
          }}
        />
      );
    };

    // Previews the currently resolved voice at the stored rate. When no
    // voice is explicitly picked, leaving `voice` undefined lets the server
    // fall back to the default Azure voice — same voice the user hears when
    // actually using the feature.
    const previewResolvedVoice = (feature: tts.TTSFeature) => {
      const voiceId = tts.getVoicePickerState({
        user: stores.user,
        feature,
        language,
      }).selectedVoiceId;
      void tts.previewVoice({
        user: stores.user,
        feature,
        language,
        voiceId,
      });
    };

    const voiceDisplayName = (feature: tts.TTSFeature) =>
      tts.getVoicePickerState({ user: stores.user, feature, language })
        .selectedVoiceName ?? "Not set";

    const speakingModeEnabled = Boolean(stores.user.state.value.speakingMode);
    const setSpeakingMode = (next: boolean) => {
      if (speakingModeControlsDisabled) return;
      if (next === speakingModeEnabled) return;
      speakingModeControls.setSpeakingModeWithVoicePrompt(next, language);
    };

    if (this.props.variant === "settings") {
      // Voice + Speed laid out in the page's standard settings table so the
      // labels line up and share the page's font and spacing.
      const voiceSpeedTable = (feature: tts.TTSFeature, disabled = false) => {
        const currentRate = tts.getRate({
          user: stores.user,
          feature,
          language,
        });
        return (
          <table class="settingsTable">
            <tbody>
              <tr>
                <td>Voice</td>
                <td>
                  <span class="ttsSettingsVoiceName">
                    {voiceDisplayName(feature)}
                  </span>
                  <button
                    type="button"
                    class="ttsSettingsChangeVoice"
                    disabled={disabled}
                    onClick={() => openPicker(feature)}
                  >
                    Change…
                  </button>
                </td>
              </tr>
              <tr>
                <td>Speed</td>
                <td>
                  <select
                    disabled={disabled}
                    value={String(currentRate)}
                    onChange={async (event) => {
                      const rate = Number(event.currentTarget.value);
                      await tts.setRate({
                        user: stores.user,
                        feature,
                        language,
                        rate,
                      });
                      this.forceUpdate();
                      // No global playback to restart on the settings page, so
                      // just preview the resolved voice at the new rate.
                      previewResolvedVoice(feature);
                    }}
                  >
                    {ttsUi.RATE_CHOICES.map((rate) => (
                      <option value={String(rate)}>{rate}x</option>
                    ))}
                  </select>
                </td>
              </tr>
            </tbody>
          </table>
        );
      };

      return (
        <div class="ttsVoiceSettingsPage">
          {this.props.heading && <h2 class="spacing">{this.props.heading}</h2>}
          <h3>Read aloud</h3>
          <p>Used when reading the whole text aloud.</p>
          {voiceSpeedTable("readAloud")}
          {speakingModeSupported && (
            <>
              <h3>Auto-Pronounce</h3>
              <label class="ttsSettingsAutoPronounce">
                <input
                  type="checkbox"
                  checked={speakingModeEnabled}
                  disabled={speakingModeControlsDisabled}
                  onClick={() => setSpeakingMode(!speakingModeEnabled)}
                />{" "}
                Speak words and phrases as you tap them{" "}
                <small>(applies to all languages)</small>
              </label>
              <p>
                The voice is also used during flashcard practice and chat with
                Ami.
              </p>
              {voiceSpeedTable("speakingMode", speakingModeControlsDisabled)}
            </>
          )}
        </div>
      );
    }

    const ratePills = (feature: tts.TTSFeature, disabled = false) => {
      const currentRate = tts.getRate({ user: stores.user, feature, language });
      return ttsUi.RATE_CHOICES.map((rate) => (
        <span
          class={cn({
            clickable: true,
            disabled,
            readAloudRateOption: true,
            selected: rate === currentRate,
          })}
          data-value={rate}
          onClick={async () => {
            if (disabled) return;
            await tts.setRate({
              user: stores.user,
              feature,
              language,
              rate,
            });
            this.forceUpdate();
            if (
              feature === "readAloud" &&
              this.props.onReadAloudSettingsChanged
            ) {
              // Read Aloud in the reader has a global playback that should
              // restart with the new rate; falls back to playing the demo
              // otherwise.
              this.props.onReadAloudSettingsChanged();
            } else {
              // No global playback to restart (Auto-Pronounce, or Read Aloud
              // on the settings page), so just preview the resolved voice at
              // the new rate. previewVoice() reads the rate via tts (already
              // updated above).
              previewResolvedVoice(feature);
            }
          }}
        >
          {rate}X
        </span>
      ));
    };

    const activityIndicator = (active: boolean) =>
      active ? (
        <span class="ttsActivityIndicator" aria-hidden="true">
          <span></span>
          <span></span>
          <span></span>
        </span>
      ) : null;

    const voiceHeading = (label: string, active: boolean, title?: string) => (
      <h3 class="readAloudVoice readAloudVoiceHeading" title={title}>
        {label}
        {activityIndicator(active)}
      </h3>
    );

    const voiceRow = (feature: tts.TTSFeature, disabled = false) => {
      const displayName =
        tts.getVoicePickerState({ user: stores.user, feature, language })
          .selectedVoiceName ?? "Not set";
      return (
        <p class="readAloudVoiceSummary">
          <span class="readAloudVoiceSummaryName">Voice: {displayName}</span>
          <span
            class={cn({
              readAloudVoiceChooseButton: true,
              clickable: true,
              disabled,
            })}
            onClick={() => openPicker(feature)}
          >
            Choose voice…
          </span>
        </p>
      );
    };

    return (
      <div class="ttsVoiceSettings">
        {this.props.heading && <h2 class="spacing">{this.props.heading}</h2>}
        <div class="readAloudVoiceSetting">
          {voiceHeading("Read Aloud", readAloudActive)}
          <small class="readAloudVoiceDescription">
            Used when reading the whole text aloud.
          </small>
          {voiceRow("readAloud")}
          <p class="readAloudRate">{ratePills("readAloud")}</p>
        </div>
        {speakingModeSupported && (
          <div
            class={cn({
              readAloudVoiceSetting: true,
              speakingModeControlsDisabled,
            })}
            title={
              speakingModeControlsDisabled ? "Read Aloud is playing" : undefined
            }
          >
            {voiceHeading(
              "Auto-Pronounce",
              speakingModeActive,
              speakingModeControlsDisabled ? "Read Aloud is playing" : undefined
            )}
            <small class="readAloudVoiceDescription">
              Speaks words and phrases as you tap them. The voice is also used
              during flashcard practice and chat with Ami.
            </small>
            <p>
              <span
                class={cn({
                  clickable: true,
                  disabled: speakingModeControlsDisabled,
                  selected: speakingModeEnabled,
                })}
                onClick={() => setSpeakingMode(true)}
              >
                Speaking On
              </span>
              <span
                class={cn({
                  clickable: true,
                  disabled: speakingModeControlsDisabled,
                  selected: !speakingModeEnabled,
                })}
                onClick={() => setSpeakingMode(false)}
              >
                Speaking Off
              </span>
            </p>
            {voiceRow("speakingMode", speakingModeControlsDisabled)}
            <p class="readAloudRate">
              {ratePills("speakingMode", speakingModeControlsDisabled)}
            </p>
          </div>
        )}
      </div>
    );
  }
}

export default TTSVoiceSettingsView;
